import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  ArrowRight, Filter, Grid } from 'lucide-react';

const WashU: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Washington University in St. Louis";

    return (
        <div className="min-h-screen bg-white font-sans text-[#242424] selection:bg-[#a52a2a] selection:text-white">
            {/* Top Bar - Green */}
            <div className="bg-[#007360] text-white py-2 px-6 flex justify-end gap-6 text-xs font-bold uppercase tracking-wide">
                <a href="#" className="hover:underline">Directory</a>
                <a href="#" className="hover:underline">Maps</a>
                <a href="#" className="hover:underline">Safety</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-200">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col font-serif leading-none text-[#a52a2a]">
                        <span className="text-xl">{schoolName}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#333] tracking-wide text-sm">
                        <a href="#" className="hover:text-[#a52a2a] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#a52a2a] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#a52a2a] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#a52a2a] transition-colors">Campus Life</a>
                        <a href="#" className="hover:text-[#a52a2a] transition-colors">About</a>
                    </div>

                    <div className="flex gap-4">
                        <Search className="cursor-pointer text-[#333] hover:text-[#a52a2a]" size={22} />
                        <Menu className="cursor-pointer text-[#333] lg:hidden" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Split Screen / Minimal */}
            <header className="relative h-[600px] grid grid-cols-1 md:grid-cols-2">
                 <div className="bg-[#f4f4f4] flex flex-col justify-center p-12 md:p-20 order-2 md:order-1">
                     <span className="text-[#a52a2a] font-bold uppercase tracking-widest text-xs mb-6">Distinction</span>
                     <h1 className="text-4xl md:text-6xl font-serif text-[#242424] mb-8 leading-tight">
                         Knowing by Name and Story.
                     </h1>
                     <p className="text-lg text-gray-600 mb-10 leading-relaxed font-light">
                         A community where you are known and valued. Where we are committed to your success.
                     </p>
                     <Button className="bg-[#a52a2a] hover:bg-[#802020] text-white font-bold px-8 h-12 rounded-sm w-fit">
                         Meet {schoolName.split(' ')[0]}
                     </Button>
                 </div>
                 <div className="relative order-1 md:order-2 h-full">
                     <img 
                        src="https://images.unsplash.com/photo-1549650178-9e120d5757d9?q=80&w=1974&auto=format&fit=crop" 
                        alt="Brookings Hall" 
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                 </div>
            </header>

            {/* Grid Layout - Research & Community */}
            <section className="bg-white py-24 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto">
                     <div className="flex justify-between items-center mb-12">
                         <h2 className="text-3xl font-serif text-[#242424]">Discoveries & Impact</h2>
                         <div className="flex gap-2">
                             <Button variant="outline" size="icon" className="rounded-full"><Filter size={16}/></Button>
                             <Button variant="outline" size="icon" className="rounded-full"><Grid size={16}/></Button>
                         </div>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                         <div className="lg:col-span-2 group cursor-pointer">
                             <img src="https://images.unsplash.com/photo-1576091160550-21878bf72a5b?q=80&w=2070&auto=format&fit=crop" className="w-full h-[400px] object-cover mb-4 grayscale group-hover:grayscale-0 transition-all duration-500" alt="Medicine" />
                             <span className="text-[#007360] font-bold uppercase text-[10px] tracking-widest block mb-2">Medicine</span>
                             <h3 className="text-2xl font-serif font-bold group-hover:text-[#a52a2a] transition-colors">School of Medicine leads genomic study on heart disease</h3>
                         </div>
                         
                         <div className="flex flex-col justify-between p-6 bg-[#f9f9f9] group cursor-pointer hover:bg-[#a52a2a] hover:text-white transition-colors">
                              <div>
                                  <span className="text-gray-400 font-bold uppercase text-[10px] tracking-widest block mb-4 group-hover:text-white/70">Arts & Sciences</span>
                                  <h3 className="text-xl font-serif font-bold">New Humanities center opens on Danforth Campus</h3>
                              </div>
                              <ArrowRight className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                         </div>

                         <div className="group cursor-pointer">
                              <img src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=2070&auto=format&fit=crop" className="w-full h-48 object-cover mb-4 grayscale group-hover:grayscale-0 transition-all duration-500" alt="Students" />
                              <span className="text-[#007360] font-bold uppercase text-[10px] tracking-widest block mb-2">Student Life</span>
                              <h3 className="text-lg font-serif font-bold group-hover:text-[#a52a2a] transition-colors">Residential Colleges foster close-knit community</h3>
                         </div>
                     </div>
                 </div>
            </section>

             {/* Footer - Combined Color */}
            <footer className="bg-[#242424] text-white py-16 px-6 md:px-12 border-t-8 border-[#a52a2a]">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 text-sm">
                    <div className="col-span-1 md:col-span-2">
                         <div className="font-serif text-2xl mb-6 leading-none">
                            {schoolName}
                         </div>
                         <p className="text-gray-400 mb-6 max-w-xs">
                             One Brookings Drive <br/>
                             St. Louis, MO 63130 <br/>
                             314-935-5000
                         </p>
                    </div>

                    <div>
                        <h4 className="text-[#007360] font-bold uppercase tracking-widest mb-6 text-xs">Resources</h4>
                        <ul className="space-y-3 font-bold text-gray-300">
                            <li><a href="#" className="hover:text-white">Athletics</a></li>
                            <li><a href="#" className="hover:text-white">Human Resources</a></li>
                            <li><a href="#" className="hover:text-white">Emergency Management</a></li>
                             <li><a href="#" className="hover:text-white">Web Accessibility</a></li>
                        </ul>
                    </div>

                     <div>
                        <h4 className="text-[#007360] font-bold uppercase tracking-widest mb-6 text-xs">Connect</h4>
                        <ul className="space-y-3 font-bold text-gray-300">
                             <li><a href="#" className="hover:text-white">Facebook</a></li>
                             <li><a href="#" className="hover:text-white">Instagram</a></li>
                             <li><a href="#" className="hover:text-white">LinkedIn</a></li>
                             <li><a href="#" className="hover:text-white">YouTube</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-[1200px] mx-auto mt-12 pt-8 border-t border-gray-800 text-center text-xs text-gray-500">
                    © 2026 {schoolName}
                </div>
            </footer>
        </div>
    );
};

export default WashU;
